package components

import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.dom.create
import kotlinx.html.js.div
import mainScope
import net.gorillagroove.notification.NotificationSetting
import net.gorillagroove.notification.NotificationSettingMethod
import net.gorillagroove.notification.UserNotificationService
import onSubmitSuspend
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTableElement
import query
import queryId

private val table get() = document.queryId<HTMLTableElement>("user-notification-settings-table")
private val tableBody get() = table.querySelector("table tbody") as HTMLElement

private const val ACTION_BUTTON_ID = "submit-notifications-button"

@Suppress("FunctionName")
fun UserNotificationSettings() = document.create.div {
    id = "user-notification-settings"

    h1 {
        + "Notification Settings"
    }

    form {
        onSubmitSuspend = {
            UserNotificationSettingsModal.submit()
        }

        table("data-table") {
            id = "user-notification-settings-table"

            thead {
                tr {
                    th { +"Event" }
                    th { +"Method" }
                    th {
                        +"Push Delay (Minutes)"

                        i("fa-solid fa-circle-info ml-6") {
                            tooltip = "Notifications will not be sent until no event has been received in this amount of time.\n\nAll notifications received in this window will be grouped up and sent as a single notification.\nMaking this a larger delay will essentially reduce the number of individual push notifications.\n\nSetting this value to 0 will disable delays, and all notifications will be sent as they happen with no grouping."
                            tooltipDelay = 100
                        }
                    }
                }
            }
            tbody {

            }
        }

        div("mt-12") {
            ActionButton(ACTION_BUTTON_ID, "Submit")
        }
    }

    mainScope.launch {
        Tooltip.registerAll(table)
        UserNotificationSettingsModal.setLoading(true)

        UserNotificationSettingsModal.initialize()
        UserNotificationSettingsModal.render()
    }
}

private object UserNotificationSettingsModal {
    private lateinit var settings: List<NotificationSetting>

    suspend fun initialize() {
        settings = try {
            UserNotificationService.getSettings()
        } catch (_: Exception) {
            Toast.error("Failed to load notification settings")
            setLoading(false)
            emptyList()
        }
    }

    fun setLoading(loading: Boolean) {
        actionButtonChangeState(ACTION_BUTTON_ID, loading)
    }

    fun render() {
        setLoading(true)

        tableBody.innerHTML = ""
        tableBody.append {
            settings.forEach { setting ->
                tr {
                    id = "setting-${setting.type}"

                    td {
                        + setting.displayString
                    }
                    td {
                        select(classes = "mt-4 mb-4") {
                            NotificationSettingMethod.entries
                                .filterNot { it == NotificationSettingMethod.UNKNOWN }
                                .forEach { method ->
                                    option {
                                        value = method.name
                                        selected = setting.method == method
                                        +method.name
                                    }
                                }
                        }
                    }
                    td {
                        input {
                            value = setting.batchIntervalMinutes.toString()
                            type = InputType.number
                            min = "0"
                        }
                    }
                }
            }
        }

        setLoading(false)
    }

    suspend fun submit() {
        val settings = settings.map { setting ->
            val row = document.queryId<HTMLElement>("setting-${setting.type}")
            val methodSelect = row.query<HTMLSelectElement>("select")
            val batchIntervalInput = row.query<HTMLSelectElement>("input")

            val method = NotificationSettingMethod.valueOf(methodSelect.value)
            val batchInterval = batchIntervalInput.value.toIntOrNull()?.takeIf { it >= 0 } ?: run {
                Toast.error("Setting '${setting.displayString}' must be a positive number")
                batchIntervalInput.focus()
                return
            }

            setting.method = method
            setting.batchIntervalMinutes = batchInterval
            setting
        }

        setLoading(true)
        val success = UserNotificationService.updateSettings(settings)
        if (success) {
            Toast.success("Notification settings updated")
        } else {
            Toast.info("Notification setting were not updated. They will be retried later automatically")
        }
        Dialog.remove()
        setLoading(false)
    }
}
